import { useState } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import parsePhoneNumber, { validatePhoneNumberLength } from 'libphonenumber-js';
import { SingleValue } from 'react-select';

import { renderMultilineText } from '@/utils/renderMultilineText';
import { getContactOptions } from '@/utils/getContactOptions';
import { useModal } from '@/hooks/useModal';
import { useNotification } from '@/hooks/useNotification';

import { Title } from '@/components/Title';
import { Button } from '@/components/Button';
import { TextInput } from '../TextInput';
import { ContactOption, ContactSelect } from '../ContactSelect';

import styles from './LeadForm.module.scss';

const URL = '/send';

const INPUTS_PATTERNS = {
  name: {
    minSymbols: 2,
    maxSymbols: 50,
    regExp: /^[a-zA-Zа-яА-ЯёЁіІїЇєЄçÇéÉèÈêÊëËàÀâÂäÄùÙûÛüÜôÔöÖœŒîÎïÏ' -]+$/,
  },
  phone: {
    regExp: /^[\d\s()+-]+$/,
  },
};

interface LeadFormProps {
  modal?: boolean;
}

export function LeadForm({ modal }: LeadFormProps) {
  const { t } = useTranslation('leadForm');

  const contactOptions = getContactOptions(t);

  const [name, setName] = useState('');
  const [contact, setContact] = useState(contactOptions[0]);
  const [phoneOrTag, setPhoneOrTag] = useState('');
  const [errors, setErrors] = useState({
    name: '',
    phoneOrTag: '',
  });
  const [isFormDisabled, setIsFormDisabled] = useState(false);

  const { showNotification } = useNotification();
  const { closeModal } = useModal();

  const handleContactChange = (selectedOption: SingleValue<ContactOption>) => {
    if (selectedOption) {
      setContact(selectedOption);
    }
  };

  const validateTextField = ({
    minSymbols,
    maxSymbols,
    regExp,
    value,
    type = 'text',
  }: {
    regExp: RegExp;
    minSymbols?: number;
    maxSymbols?: number;
    value?: string;
    type?: 'text' | 'phone';
  }) => {
    if (!value) {
      return t('errors.required');
    }

    if (type === 'phone') {
      const phoneNumber = parsePhoneNumber(value);

      if (!regExp.test(value)) {
        return t('errors.notAllowedSymbols');
      }
      if (!phoneNumber) {
        return t('errors.phone.notInternational');
      }
      if (validatePhoneNumberLength(phoneNumber.formatInternational()) === 'TOO_SHORT') {
        return t('errors.phone.tooShort');
      }
      if (validatePhoneNumberLength(phoneNumber.formatInternational()) === 'TOO_LONG') {
        return t('errors.phone.tooLong');
      }
      if (!phoneNumber.isValid()) {
        return t('errors.phone.invalid');
      }
    } else if (minSymbols && maxSymbols) {
      if (!regExp.test(value)) {
        return t('errors.notAllowedSymbols');
      }
    } else {
      if (!regExp.test(value)) {
        return t('errors.invalidValue');
      }
    }

    if (minSymbols && maxSymbols && (value.length < minSymbols || value.length > maxSymbols)) {
      return t('errors.invalidLength');
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {
      name: validateTextField({ ...INPUTS_PATTERNS.name, value: name.trim() }),
      phoneOrTag: validateTextField({
        regExp: INPUTS_PATTERNS.phone.regExp,
        value: phoneOrTag,
        type: 'phone',
      }),
    };

    setErrors(newErrors);
    return !newErrors.name && !newErrors.phoneOrTag;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm();
    if (isValid) {
      const requestBody = JSON.stringify({
        name,
        contact: contact.value,
        phoneOrTag: phoneOrTag.trim(),
      });
      setIsFormDisabled(true);
      try {
        const response = await axios.post(URL, requestBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.data === 'OK') {
          showNotification(t('success'), 'success');
          setName('');
          setPhoneOrTag('');
          if (modal) {
            closeModal();
          }
        } else {
          throw new Error('Error response from server');
        }
      } catch (error) {
        showNotification(t('error'), 'error');
        // For the first tests
        // eslint-disable-next-line no-console
        console.error('Error sending form data: ', error);
      } finally {
        setIsFormDisabled(false);
      }
    }
  };

  return (
    <form
      className={clsx(styles.leadForm, {
        [styles.leadForm_modal]: modal,
        [styles.leadForm_disabled]: isFormDisabled,
      })}
      onSubmit={handleSubmit}
    >
      <div className={styles.leadForm__content}>
        {!modal && <Title className={styles.leadForm__title}>{t('title')}</Title>}
        <p className={styles.leadForm__description}>
          {renderMultilineText(modal ? t('modalDescription') : t('description'))}
        </p>
      </div>
      <div className={styles.leadForm__inputs}>
        <TextInput
          className={styles.leadForm__input}
          type="text"
          placeholder={t('placeholders.name')}
          autoComplete="given-name"
          value={name}
          error={errors.name}
          onChange={(e) => setName(e.target.value)}
        />
        <ContactSelect
          isForModal={modal}
          options={contactOptions}
          value={contact}
          onChange={handleContactChange}
        />
        <TextInput
          className={styles.leadForm__input}
          type="tel"
          placeholder={t('placeholders.phone')}
          autoComplete="tel"
          value={phoneOrTag}
          error={errors.phoneOrTag}
          onChange={(e) => setPhoneOrTag(e.target.value)}
        />
      </div>
      <Button className={styles.leadForm__button} type="submit">
        {modal ? t('send') : t('getConsultation')}
      </Button>
    </form>
  );
}
